import { api } from "./api";
import { publicChatPayload, publicContextEnvelope } from "./vibebeeConversation.mjs";
export { cancelVoiceInput, startVoiceInput, supportsVoiceInput, VOICE_UI_STATES } from "./vibebeeVoice";

let cachedVibeBeeCsrfToken = null;
let memorySessionId = null;

function sessionId() {
  if (typeof window === "undefined") return "server";
  const key = "v2n_vibebee_session_id";
  try {
    const stored = window.sessionStorage.getItem(key);
    if (stored) memorySessionId = stored;
    if (!memorySessionId) memorySessionId = `vibebee-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    if (!stored) window.sessionStorage.setItem(key, memorySessionId);
  } catch (_) {
    // Some Safari/privacy/webview contexts deny storage; chat still works in memory.
    if (!memorySessionId) memorySessionId = `vibebee-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
  return memorySessionId;
}

async function adminCsrfConfig() {
  if (!cachedVibeBeeCsrfToken) {
    const response = await api.get("/admin/csrf");
    cachedVibeBeeCsrfToken = response.data.csrf_token;
  }
  return {
    headers: {
      "X-CSRF-Token": cachedVibeBeeCsrfToken,
    },
  };
}

function payload(message, history = [], location = null) {
  return {
    message,
    client_session_id: sessionId(),
    history: history.slice(-8).map((item) => ({ role: item.role, content: item.content })),
    location,
  };
}

export async function askPublicVibeBee(message, history = [], location = null, context = {}, inputMode = "text", visitorAction = null) {
  const response = await api.post("/vibes/vibebee/chat", publicChatPayload(message, history, context, location, sessionId(), inputMode, visitorAction));
  return response.data;
}

export async function askAdminVibeBeeLiveAgent(message, history = [], location = null, adminPosterContext = {}, context = {}, inputMode = "text") {
  // The server independently validates intent, scope and source permissions.
  // Keep ordinary Admin chat read-only; explicit research uses its CSRF action.
  if (/^(?:vibebee[,\s]+)?(?:please\s+|can you\s+|could you\s+)?(?:research|investigate|investiga|investigar)\b/i.test(message.trim())) {
    const response = await api.post("/admin/event-ingestion/vibebee-research",
      { message, input_mode: inputMode === "voice" ? "voice" : "text" },
      { ...(await adminCsrfConfig()), timeout: 150000 });
    return response.data;
  }
  const response = await api.post(
    "/vibes/vibebee/admin-live-agent/chat",
    { ...payload(message, history, location), context_envelope: publicContextEnvelope(context, "admin"), admin_poster_context: adminPosterContext, input_mode: inputMode === "voice" ? "voice" : "text", voice_requested: inputMode === "voice" },
    await adminCsrfConfig(),
  );
  return response.data;
}

export async function getAdminVibeBeeLiveStatus() {
  const response = await api.get("/vibes/vibebee/admin-live-agent/status");
  return response.data;
}

export async function getAdminVibeBeeJourneyTracking(days = 30) {
  const response = await api.get("/admin/vibebee/journey-tracking", { params: { days } });
  return response.data;
}

export async function getAdminVibeBeeVenueIntelligenceCoverage() {
  const response = await api.get("/admin/vibebee/venue-intelligence-coverage");
  return response.data;
}

export async function getAdminVenueIntelligenceQueue(days = 90, limit = 50, view = null) {
  const response = await api.get("/admin/venue-intelligence/queue", {
    params: { days, limit, ...(view ? { view } : {}) },
  });
  return response.data;
}

export async function getPublicVibeBeeCapabilities() {
  const response = await api.get("/vibes/vibebee/capabilities");
  return response.data;
}

export async function requestVibeBeeSpeech(presentation, { signal } = {}) {
  const authentication = presentation.surface === "admin" ? await adminCsrfConfig() : {};
  const response = await api.post(
    "/vibes/vibebee/speech",
    {
      response_id: presentation.response_id,
      synthesis_token: presentation.synthesis_token,
    },
    {
      responseType: "blob",
      signal,
      timeout: 15000,
      ...authentication,
    },
  );
  return response.data;
}

export async function recordVibeBeeVoiceEvent(event, responseId, outcome, latencyMs = 0, failureClass = "") {
  try {
    await api.post("/vibes/vibebee/voice-event", {
      event,
      response_id: responseId,
      outcome,
      latency_ms: Math.max(0, Math.round(latencyMs || 0)),
      failure_class: failureClass,
    });
  } catch (_) {
    // Voice telemetry is operational only and must never affect playback.
  }
}

